import {
  IGeneralEligibilityRequirement,
  IPayeeResponse,
  IPayerResponse,
  PayeeTaxDocumentSharePermission,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { GeneralRequirementType } from "@wingspanhq/payments/dist/interfaces";
import {
  WSPillProps,
  WSIconName,
  WSTableRowMenuActions,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { usePayeeQuery } from "../../../../query/payee/queries/usePayeeQuery";
import { usePayeeDownloadW9 } from "../../../../query/payee/mutations/usePayeeDownloadW9";
import { RequirementAction, RequirementContextType } from "../../types";
import { getIsDomesticMember } from "../../../../query/users/selectors";
import { CustomRequirementType, ExtendedGeneralRequirement } from "./types";
import { useModalShareTaxInformationWithPayers } from "../../../../shared/components/ModalShareTaxInformationWithPayers/useModalShareTaxInformationWithPayers";

const mapGeneralRequirementTypeToName: Record<
  GeneralRequirementType | CustomRequirementType,
  string
> = {
  [GeneralRequirementType.PayoutMethod]: "Payout method",
  [GeneralRequirementType.TaxVerification]: "Taxpayer information",
  [GeneralRequirementType.Registration]: "Sign up",
  [CustomRequirementType.TaxForm]: "Taxpayer information",
  [CustomRequirementType.InfoShare]: "Consent to share information"
};

const mapGeneralRequirementTypeToType: Record<
  GeneralRequirementType | CustomRequirementType,
  string
> = {
  [GeneralRequirementType.PayoutMethod]: "Bank account",
  [GeneralRequirementType.TaxVerification]: "Form",
  [GeneralRequirementType.Registration]: "Onboarding",
  [CustomRequirementType.TaxForm]: "Form",
  [CustomRequirementType.InfoShare]: "Info sharing"
};

const mapGeneralRequirementTypeToStatus: Record<
  RequirementContextType,
  Record<RequirementStatus, WSPillProps>
> = {
  [RequirementContextType.Payer]: {
    [RequirementStatus.Complete]: {
      theme: "success",
      text: "Complete",
      icon: true
    },
    [RequirementStatus.Incomplete]: {
      theme: "warning",
      text: "Action required",
      icon: true
    },
    [RequirementStatus.PendingReview]: {
      theme: "warning",
      text: "Pending",
      icon: true
    }
  },
  [RequirementContextType.Payee]: {
    [RequirementStatus.Complete]: {
      theme: "success",
      text: "Complete",
      icon: true
    },
    [RequirementStatus.Incomplete]: {
      theme: "warning",
      text: "Pending",
      icon: true
    },
    [RequirementStatus.PendingReview]: {
      theme: "warning",
      text: "Action required",
      icon: true
    }
  }
};

const mapGeneralRequirementTypeToIcon: Record<
  GeneralRequirementType | CustomRequirementType,
  WSIconName
> = {
  [GeneralRequirementType.PayoutMethod]: "bank",
  [GeneralRequirementType.TaxVerification]: "clipboard-check",
  [GeneralRequirementType.Registration]: "user-check",
  [CustomRequirementType.TaxForm]: "clipboard-check",
  [CustomRequirementType.InfoShare]: "clipboard-check"
};

export type GeneralRequirementsConfigParams = {
  payerId: string | undefined;
  payeeId: string | undefined;
  payee?: IPayeeResponse;
  payer?: IPayerResponse;
  contextType: RequirementContextType;
};

export const useGeneralRequirementsConfig = (
  props: GeneralRequirementsConfigParams
) => {
  const { payee, payer, contextType } = props;

  const [downloadW9, downloadW9Meta] = usePayeeDownloadW9();
  const modalFormW9Consent = useModalShareTaxInformationWithPayers();

  const generalRequirements = useMemo(() => {
    return (
      payee?.requirements?.filter(
        requirement =>
          requirement.requirementType !== GeneralRequirementType.PayoutMethod
      ) || []
    );
  }, [payee?.requirements]);

  const isDomesticPayee = payee?.member
    ? getIsDomesticMember(payee?.member)
    : true;

  return {
    isLoading: false,
    dataUpdatedAt: 1,
    generalRequirements,
    getEngagementsCellProps: (requirement: ExtendedGeneralRequirement) => ({
      text: "All"
    }),
    getNameCellProps: (requirement: ExtendedGeneralRequirement) => ({
      text: mapGeneralRequirementTypeToName[requirement.requirementType]
    }),
    getTypeCellProps: (requirement: ExtendedGeneralRequirement) => ({
      text: mapGeneralRequirementTypeToType[requirement.requirementType],
      icon: mapGeneralRequirementTypeToIcon[requirement.requirementType]
    }),
    getStatusCellProps: (requirement: ExtendedGeneralRequirement) => {
      let secondaryText = undefined;

      if ("allPayersLength" in requirement) {
        secondaryText = `Shared with ${requirement.completedPayersLength} of ${requirement.allPayersLength} clients`;
      }

      return {
        pill: mapGeneralRequirementTypeToStatus[contextType][
          requirement.status
        ],
        secondaryText
      };
    },
    getRowMenuActions: (requirement: ExtendedGeneralRequirement) => {
      const actions: RequirementAction[] = [];

      if (
        payee?.payeeOwnedData.shareTaxDocument ===
          PayeeTaxDocumentSharePermission.Allow &&
        isDomesticPayee &&
        requirement.requirementType ===
          GeneralRequirementType.TaxVerification &&
        requirement.status === RequirementStatus.Complete
      ) {
        actions.push({
          label: "Download PDF",
          icon: "download",
          onClick: () => {
            downloadW9({
              payeeId: payee.payeeId,
              filename:
                payee.user?.email ||
                `${toWSDateString(new Date(), "monthDayYear")}`
            });
          }
        });
      }

      if (
        requirement.requirementType === CustomRequirementType.InfoShare &&
        requirement.status === RequirementStatus.Incomplete &&
        contextType === RequirementContextType.Payer
      ) {
        actions.push({
          label: "Consent to share information",
          icon: "clipboard-check",
          onClick: () => {
            modalFormW9Consent.open({
              payerId: requirement.payerId
            });
          }
        });
      }

      return actions;
    }
  };
};
